/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { get } from 'js/lib/user';

import { Avatar, Tag, typography2 } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';
import { track } from '@coursera/event-pulse/sdk';

import { MOBILE_MAIN_IDENTIFIER } from 'bundles/composable-page-header/constants';
import { useMobileToSubPage } from 'bundles/composable-page-header/contexts/MobileNavigationContext';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  hasCourseraPlus?: boolean;
};

const styles = {
  fullName: css`
    align-self: center;
    color: var(--cds-color-neutral-primary);
    display: -webkit-box;
    font: ${typography2.subtitleMedium.font};
    letter-spacing: ${typography2.subtitleMedium.letterSpacing};
    line-height: var(--cds-line-height-250);
    margin-left: var(--cds-spacing-100);
    max-height: calc(3 * var(--cds-line-height-200));
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  `,
  nameAndPicture: css`
    display: flex;
    flex-direction: row;
  `,
  navButton: css`
    width: 100%;
    background-color: transparent;
    border: none;
    text-align: left;
    position: relative;
    padding: var(--cds-spacing-100);
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--cds-color-neutral-primary);
    margin-bottom: var(--cds-spacing-100);
  `,
  avatar: css`
    &.cds-Avatar-base {
      overflow: visible;
    }
  `,
  avatarInExperiment: css`
    &.cds-Avatar-showBorder {
      border: 2px solid var(--cds-color-interactive-primary);
    }
  `,
  arrow: css`
    flex-shrink: 0;
  `,
  plusBadge: css`
    padding: 0 6px;
    position: relative;
    top: -10px;
    height: 10px;

    span {
      font-size: 8px;
    }

    &.cds-tag-status.cds-tag-primary.cds-tag-default {
      background-color: var(--cds-color-interactive-primary);
      border-color: var(--cds-color-interactive-primary);
    }
  `,
  border: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: -10px;
  `,
};

const AccountMenuItem = ({ hasCourseraPlus }: Props) => {
  const { fullName, photo: defaultPhoto, photo_120: photoUrl } = get();
  const toSubPage = useMobileToSubPage();

  return (
    <TrackedButton
      onClick={() => {
        track('open_navigation_menu', {
          navigationType: 'profile',
        });
        toSubPage?.(MOBILE_MAIN_IDENTIFIER.ACCOUNT_MENU);
      }}
      css={styles.navButton}
      trackingName="mobile_header_account_button"
      aria-label={_t('Go to account navigation slide')}
      tabIndex={0}
    >
      <div css={styles.nameAndPicture}>
        <div css={hasCourseraPlus && styles.border}>
          <Avatar
            variant="user"
            data-testid="user-avatar"
            initial={fullName?.[0]}
            imgProps={{
              src: photoUrl || defaultPhoto,
              alt: _t('Profile image for #{learnerName}', { learnerName: fullName }),
            }}
            css={[styles.avatar, hasCourseraPlus && styles.avatarInExperiment]}
            showBorder={hasCourseraPlus}
          />
          {hasCourseraPlus && (
            <Tag variant="status" priority="primary" css={styles.plusBadge}>
              PLUS
            </Tag>
          )}
        </div>
        <span css={styles.fullName}>{fullName}</span>
      </div>
      <ChevronNextIcon css={styles.arrow} color="default" size="medium" />
    </TrackedButton>
  );
};

export default AccountMenuItem;
